import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import api, { formatApiError } from "@/lib/api";
import { X, CheckCircle2, Phone, User, Mail, AlertTriangle, Upload } from "lucide-react";
import Avatar, { AVATAR_KEYS, isCustomAvatar } from "@/components/Avatar";
import { useAuth } from "@/contexts/AuthContext";

/** Crop the middle square of whatever they picked and shrink it to 256px — small
 *  enough to live on the account, big enough for a crisp header badge. */
const resizeToSquare = (file, px = 256) => new Promise((resolve, reject) => {
  const img = new Image();
  const fr = new FileReader();
  img.onload = () => {
    const s = Math.min(img.width, img.height);
    const c = document.createElement("canvas");
    c.width = px; c.height = px;
    c.getContext("2d").drawImage(img, (img.width - s) / 2, (img.height - s) / 2, s, s, 0, 0, px, px);
    resolve(c.toDataURL("image/jpeg", 0.86));
  };
  img.onerror = () => reject(new Error("decode"));
  fr.onload = () => { img.src = fr.result; };
  fr.onerror = () => reject(new Error("read"));
  fr.readAsDataURL(file);
});

/** Your handle: name, phone and a picked avatar. Everything else stays with the admin. */
export default function ProfileModal({ open, onClose }) {
  const { user, setUser } = useAuth();
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [phone, setPhone] = useState("");
  const [avatar, setAvatar] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    const parts = (user.name || "").trim().split(/\s+/);
    setFirst(user.first_name || parts[0] || "");
    setLast(user.last_name || parts.slice(1).join(" ") || "");
    setPhone(user.phone || "");
    setAvatar(user.avatar || null);
    setError(""); setDone(false);
  }, [open, user]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault(); setError("");
    if (!first.trim()) { setError("Please add your first name."); return; }
    if (phone.replace(/\D/g, "").length < 7) { setError("Please add a phone number we can actually reach you on."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/me/profile", {
        first_name: first.trim(), last_name: last.trim(), phone: phone.trim(), avatar: avatar || "",
      });
      setUser(data);
      setDone(true);
      setTimeout(onClose, 900);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" data-testid="profile-modal">
      <div className="absolute inset-0" style={{ background: "rgba(15,18,38,0.42)", backdropFilter: "blur(4px)" }} onClick={onClose} />
      <form onSubmit={submit} className="relative w-full max-w-[520px] rounded-2xl overflow-hidden fade-up"
        style={{ background: "white", boxShadow: "0 30px 70px rgba(15,18,38,0.28)" }}>
        <div className="flex items-center gap-3 px-6 pt-5 pb-4" style={{ borderBottom: "1px solid rgba(15,18,38,0.07)" }}>
          <Avatar seed={avatar} name={`${first} ${last}`} email={user?.email} size={44} testid="profile-modal-avatar" />
          <div className="min-w-0">
            <h3 className="font-display text-xl font-semibold tracking-tight leading-tight">Your handle</h3>
            <div className="text-[12px] text-ink-3 flex items-center gap-1.5 truncate"><Mail size={11} /> {user?.email}</div>
          </div>
          <button type="button" onClick={onClose} className="ml-auto w-8 h-8 rounded-lg flex items-center justify-center hover:bg-black/5" data-testid="profile-modal-close"><X size={16} /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="kpi-label">First name</span>
              <input value={first} onChange={(e) => setFirst(e.target.value)} data-testid="profile-first-name"
                className="w-full mt-1 px-3 py-2.5 rounded-xl text-sm" style={{ border: "1.5px solid rgba(15,18,38,0.12)" }} placeholder="Stuart" />
            </label>
            <label className="block">
              <span className="kpi-label">Last name</span>
              <input value={last} onChange={(e) => setLast(e.target.value)} data-testid="profile-last-name"
                className="w-full mt-1 px-3 py-2.5 rounded-xl text-sm" style={{ border: "1.5px solid rgba(15,18,38,0.12)" }} placeholder="Bell" />
            </label>
          </div>

          <label className="block">
            <span className="kpi-label flex items-center gap-1.5"><Phone size={11} /> Phone number {!user?.phone && <span style={{ color: "#B45309" }}>· missing</span>}</span>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" data-testid="profile-phone"
              className="w-full mt-1 px-3 py-2.5 rounded-xl text-sm font-mono"
              style={{ border: `1.5px solid ${user?.phone ? "rgba(15,18,38,0.12)" : "rgba(245,158,11,0.55)"}`, background: user?.phone ? undefined : "rgba(245,158,11,0.05)" }}
              placeholder="+1 555 123 4567" />
            {!user?.phone && (
              <span className="text-[11px] flex items-center gap-1.5 mt-1.5" style={{ color: "#B45309" }}>
                <AlertTriangle size={11} /> We need a number to reach you about your wallet — it's never shared with your team.
              </span>
            )}
          </label>

          <div>
            <span className="kpi-label flex items-center gap-1.5"><User size={11} /> Pick your face</span>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="profile-avatar-grid">
              {isCustomAvatar(avatar) && (
                <span className="rounded-full" data-testid="profile-avatar-photo"
                  style={{ padding: 2, background: "linear-gradient(135deg,#4060FF,#8D1CEB)" }}>
                  <Avatar seed={avatar} size={34} />
                </span>
              )}
              {AVATAR_KEYS.map((k) => (
                <button key={k} type="button" onClick={() => setAvatar(k)} data-testid={`profile-avatar-${k}`}
                  className="rounded-full transition-transform hover:scale-110"
                  style={{ padding: 2, background: avatar === k ? "linear-gradient(135deg,#4060FF,#8D1CEB)" : "transparent" }}>
                  <Avatar seed={k} size={34} />
                </button>
              ))}
            </div>
            <label className="inline-flex items-center gap-1.5 mt-3 text-[12px] font-semibold cursor-pointer"
              style={{ color: "#4060FF" }} data-testid="profile-avatar-upload-label">
              <Upload size={13} /> {isCustomAvatar(avatar) ? "Use a different photo" : "Or use my own photo"}
              <input type="file" accept="image/*" className="hidden" data-testid="profile-avatar-upload"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (!f) return;
                  if (!/^image\//.test(f.type)) { setError("Pick an image file — PNG, JPEG or WebP."); return; }
                  try { setAvatar(await resizeToSquare(f)); setError(""); }
                  catch { setError("We couldn't read that image — try another one."); }
                }} />
            </label>
          </div>

          {error && <div className="chip chip-rose" data-testid="profile-error">{error}</div>}
        </div>

        <div className="px-6 pb-5 flex items-center gap-2 justify-end">
          <button type="button" onClick={onClose} className="btn-secondary" data-testid="profile-cancel">Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary" data-testid="profile-save">
            {done ? <><CheckCircle2 size={15} /> Saved</> : busy ? "Saving…" : "Save handle"}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
